import type { HTTPMethod } from "h3";
import { singleton } from '#shared/singleton';
import { useAuth } from './useAuth';
import { ClientError } from "~/plugins/error";
import { errors  } from "~/utils/errors";
import type {ApiError} from "~/utils/errors";

/**
 * Extended fetch options with additional configuration for our API client
 */
export interface ApiRequestOptions extends Omit<RequestInit, 'body' | 'method' | 'headers'> {
  /** Request headers */
  headers?: Record<string, string>;
  /** Query parameters to be stringified and appended to the URL */
  query?: Record<string, unknown>;
  /** If true, errors won't be reported automatically */
  reportError?: boolean;
}

/**
 * API response type that includes the data and response metadata
 */
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  headers: Headers;
}

// Default headers
const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

/**
 * Composable for making API requests to the server
 * Automatically handles authentication, error reporting, and base URL
 */
export const useApi = singleton(() => {
  // Get config and dependencies
  const config = useRuntimeConfig();
  const auth = useAuth();

  /**
   * Makes a fetch request to the API with proper error handling and timeout
   */
  const fetch = async <T = unknown>(
    method: HTTPMethod,
    url: string,
    requestData?: unknown,
    options: ApiRequestOptions = {}
  ): Promise<T> => {
    const { ...fetchOptions } = options;
    
    // Prepare headers
    const headers: Record<string, string> = {
      ...defaultHeaders,
      ...(fetchOptions.headers || {})
    };
    
    const urlWithQuery = new URL(`${config.public.serverUrl}/${url}`);
    
    // Add query parameters if provided
    if (fetchOptions.query) {
      Object.entries(fetchOptions.query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          urlWithQuery.searchParams.append(key, String(value));
        }
      });
    }

    // Create fetch promise with timeout using $fetch.raw to access headers
    try {
      return await $fetch(urlWithQuery.toString(), {
        ...fetchOptions,
        method,
        headers,
        body: requestData ? JSON.stringify(requestData) : undefined,
        credentials: 'include'
      }) as T;
    } catch (error) {

      // Handle 401 Unauthorized
      if ((error as { statusCode?: number })?.statusCode === 401) {
        await auth.logout();
      }

      // Re-throw the error to propagate it up the promise chain
      if (error instanceof Error) {
        const causeIdNumber = errors.extractCauseIdNumber(error);
        if (causeIdNumber) {
          const causeId = errors.getCauseId(causeIdNumber)
          throw new ClientError(causeId, {method, url, headers, requestData, options}, undefined, undefined, error)
        } else {
          const statusCode = (error as ApiError)?.statusCode
          throw new ClientError(errors.getGeneralCauseId(statusCode), {method, url, headers, requestData, options}, undefined, undefined, error)
        }
      } else {
        throw ClientError.unexpected({method, url, headers, requestData, error, options})
      }
    }
  };

  return {
    fetch
  };
});

/**
 * Make a GET request to the server API.
 * 
 * @example
 * ```typescript
 * let myClass: MyClass;
 * get<MyClass>('/my-class').then(data => {
 *   myClass = data;
 * });
 * ```
 */
export const get = <T = unknown>(
  url: string, 
  query?: Record<string, unknown>, 
  options?: Omit<ApiRequestOptions, 'query'>
): Promise<T> => useApi().fetch<T>('GET', url, undefined, { ...options, query });

/**
 * Make a POST request to the server API.
 */
export const post = <T = unknown>(
  url: string, 
  data?: unknown, 
  options?: ApiRequestOptions
): Promise<T> => useApi().fetch<T>('POST', url, data, options);

/**
 * Make a PUT request to the server API.
 */
export const put = <T = unknown>(
  url: string, 
  data?: unknown, 
  options?: ApiRequestOptions
): Promise<T> => useApi().fetch<T>('PUT', url, data, options);

/**
 * Make a PATCH request to the server API.
 */
export const patch = <T = unknown>(
  url: string, 
  data?: unknown, 
  options?: ApiRequestOptions
): Promise<T> => useApi().fetch<T>('PATCH', url, data, options);

/**
 * Make a DELETE request to the server API.
 */
export const del = <T = unknown>(
  url: string, 
  options?: ApiRequestOptions
): Promise<T> => useApi().fetch<T>('DELETE', url, undefined, options);
